import {
    FileText,
    Calendar,
    DollarSign,
    RefreshCw,
    Clock,
    CheckCircle2,
    Zap,
    LayoutDashboard,
    ExternalLink,
} from 'lucide-react';

// ==================== Types ====================
export interface SaaSPlan {
    name: string;
    price: number;
    billingCycle?: 'monthly' | 'yearly';
}

export interface ContractDuration {
    months: number;
    startDate: string;
    renewalType: 'automatic' | 'manual';
}

interface Feature {
    name: string;
    included: boolean;
}

interface SaaSContextCardProps {
    plan: SaaSPlan;
    duration: ContractDuration;
    companyName?: string;
    pdfUrl?: string;
    onViewPdf?: () => void;
    className?: string;
}

// ==================== Default Features (SaaS) ====================
const defaultFeatures: Feature[] = [
    { name: 'Gestão Completa de Leads', included: true },
    { name: 'CRM Imobiliário Avançado', included: true },
    { name: 'Site Imobiliário Otimizado', included: true },
    { name: 'Integração com Portais', included: true },
    { name: 'Suporte Prioritário', included: true },
];

// ==================== Helpers ====================
const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value);
};

// ==================== Component ====================
export const SaaSContextCard: React.FC<SaaSContextCardProps> = ({
    plan,
    duration,
    companyName,
    pdfUrl,
    onViewPdf,
    className = '',
}) => {
    return (
        <div
            className={`bg-gradient-to-br from-sky-500/10 to-indigo-500/10 border border-sky-500/30 rounded-2xl overflow-hidden ${className}`}
        >
            {/* Header */}
            <div className="bg-white/5 border-b border-white/10 px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                        <LayoutDashboard className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">Resumo do Plano</h2>
                        {companyName && (
                            <p className="text-sm text-white/60">{companyName}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {/* Plan & Price */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {/* Plan Name */}
                    <div className="bg-white/5 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                            <Zap className="w-4 h-4" />
                            <span>Plano Escolhido</span>
                        </div>
                        <p className="text-xl font-bold text-white">{plan.name}</p>
                    </div>

                    {/* Price */}
                    <div className="bg-white/5 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                            <DollarSign className="w-4 h-4" />
                            <span>Valor Mensal</span>
                        </div>
                        <p className="text-xl font-bold text-emerald-400">
                            {formatCurrency(plan.price)}
                            <span className="text-sm font-normal text-white/50">/mês</span>
                        </p>
                    </div>
                </div>

                {/* Duration Info */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    {/* Start Date */}
                    <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                        <Calendar className="w-5 h-5 text-sky-400" />
                        <div>
                            <p className="text-xs text-white/50">Início</p>
                            <p className="text-sm font-medium text-white">
                                {formatDate(duration.startDate)}
                            </p>
                        </div>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                        <Clock className="w-5 h-5 text-amber-400" />
                        <div>
                            <p className="text-xs text-white/50">Fidelidade</p>
                            <p className="text-sm font-medium text-white">
                                {duration.months} {duration.months === 1 ? 'mês' : 'meses'}
                            </p>
                        </div>
                    </div>

                    {/* Renewal */}
                    <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                        <RefreshCw className="w-5 h-5 text-purple-400" />
                        <div>
                            <p className="text-xs text-white/50">Renovação</p>
                            <p className="text-sm font-medium text-white">
                                {duration.renewalType === 'automatic' ? 'Automática' : 'Manual'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Features Checklist */}
                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-white/70 mb-3">
                        Incluso no Pacote
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {defaultFeatures.map((feature, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-2 text-white/80 text-sm"
                            >
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                <span>{feature.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* View PDF Button */}
                {(pdfUrl || onViewPdf) && (
                    <button
                        onClick={onViewPdf}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/30 rounded-xl text-white transition-all"
                    >
                        <FileText className="w-4 h-4" />
                        <span>Ver contrato completo em PDF</span>
                        <ExternalLink className="w-4 h-4 ml-1 text-white/50" />
                    </button>
                )}
            </div>

            {/* Footer */}
            <div className="bg-white/5 border-t border-white/10 px-6 py-3">
                <p className="text-xs text-white/40 text-center">
                    Ao assinar, você concorda com a cobrança recorrente no valor acima.
                </p>
            </div>
        </div>
    );
};
